import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import Service from '@ember/service';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';
import { singularize } from '@ember-data/request-utils/string';
import * as Sentry from '@sentry/ember';
import type CurrentUserService from 'scaleflow/services/current-user';
import type SessionService from 'scaleflow/services/session';
import type StoreService from 'scaleflow/services/store';
import { authDetails } from 'scaleflow/utils/auth-session';
import { wsEndpoint } from 'scaleflow/utils/endpoints';

const WSS_ENDPOINT = wsEndpoint();

// Normalizes a websocket payload's JSON:API `type` (e.g. `SCAN`, `deliverables`)
// to this app's singular, dasherized Ember Data model name.
export function modelNameForType(type: string) {
  return dasherize(singularize(type.toLowerCase()));
}

type RelationshipRef = { id: string; type: string };

interface JsonApiResource {
  id: string | number;
  type: string;
  attributes?: Record<string, unknown> & {
    updated_at?: string;
    updated_by?: { type?: string; id?: string };
  };
  relationships?: Record<
    string,
    { data?: RelationshipRef | RelationshipRef[] } | undefined
  >;
}

/** One entry of the array a data websocket frame carries. */
interface SocketItem {
  event?: string;
  data?: JsonApiResource;
}

interface ConversationMessageFrame {
  action: 'conversation.message';
  complete: boolean;
  message: JsonApiResource;
}

type SendStatus = 'queued' | 'sent' | 'failed';

/** The slice of an Ember Data record the socket handlers read and write. */
interface SocketRecord {
  constructor: {
    relationshipNames?: { belongsTo: string[]; hasMany: string[] };
  };
  currentState?: { stateName?: string };
  updated_at?: string;
  isNew?: boolean;
  set(key: string, value: unknown): void;
  unloadRecord(): void;
  deleteRecord(): void;
}

interface ConversationRecord {
  complete: boolean;
  messages: { content: SocketRecord[] };
}

export default class DataSocketService extends Service {
  @service declare store: StoreService;
  @service('current-user') declare currentUser: CurrentUserService;
  @service declare session: SessionService;
  @service declare router: RouterService;

  socket: WebSocket | null | undefined = null;
  reconnectAttempts = 0;
  heartbeatInterval?: ReturnType<typeof setInterval>;
  _messageQueue: unknown[] = [];

  QUEUED = 'queued' as const;
  SENT = 'sent' as const;
  FAILED = 'failed' as const;

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.setupBFCacheHandling();
    this.setupVisibilityHandling();
    this.setupNetworkHandling();
  }

  get JWTToken() {
    const token = authDetails(this.session)?.accessToken;
    return token;
  }

  get tenantId() {
    const tenant_id = this.session.activeTenantId;
    return tenant_id;
  }

  async socketURL() {
    if (this.session.tokenIsExpired) {
      try {
        await this.session.refreshToken();
      } catch (err) {
        if ((err as { isTerminal?: boolean } | undefined)?.isTerminal) {
          console.debug(
            'Invalidating session due to failed token refresh',
            err,
          );
          this.router.transitionTo('logout');
        } else {
          // Transient refresh failure — do not tear down the app session over a
          // flaky socket reconnect. Back off and retry (bounded by the
          // reconnectAttempts cap in connect()).
          console.debug(
            'Transient token refresh failure, deferring socket reconnect',
            err,
          );
          this.connect(1000);
        }
        return;
      }
    }
    return `${WSS_ENDPOINT}?JWT_TOKEN=${this.JWTToken ?? ''}&tenant_id=${this.tenantId}`;
  }

  get healthy() {
    return (
      this.socket?.readyState === WebSocket.OPEN ||
      this.socket?.readyState === WebSocket.CONNECTING
    );
  }

  get unhealthy() {
    return !this.healthy;
  }

  @action
  setup() {
    if (this.socket == undefined) {
      this.connect();
    }
  }

  connect(delay = 0, backOff = true) {
    // Don't connect if session is not authenticated
    if (!this.session.isAuthenticated || !this.JWTToken || !this.tenantId) {
      console.debug('Session not authenticated, skipping WebSocket connection');
      return;
    }

    // If we have a healthy connection, do not connect.
    if (this.healthy) {
      this.reconnectAttempts = 0;
      return;
    }

    if (this.reconnectAttempts >= 5) {
      console.info('❌ Max websocket reconnect attempts reached');
      return;
    }

    if (backOff) {
      delay = Math.pow(2, this.reconnectAttempts) * delay;
    }

    this.reconnectAttempts++;

    if (delay > 0) {
      setTimeout(() => {
        this.connect(0, backOff); // This will trigger onopen when successful
      }, delay);
    } else {
      void this._connect();
    }
  }

  async _connect() {
    const url = await this.socketURL();
    // No URL means the refresh failed (terminal -> logging out, or transient ->
    // a retry was already scheduled). Either way, don't open a socket now.
    if (!url) return;
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => this.openHandler();
    socket.onmessage = (event) => this.messageHandler(event);
    socket.onclose = (event) => this.closeHandler(event);
    socket.onerror = (event) => this.errorHandler(event);
  }

  close(message?: string) {
    if (this.socket) {
      this.socket.close(1000, message);
    }
  }

  send(message: unknown): SendStatus | undefined {
    // Layer 1: Prevent obvious failures
    if (!this.socket) {
      this._queue(message);
      this.connect();
      return this.QUEUED;
    }

    // Layer 2: Be smart about message queueing.
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return this._queue(message);

      case WebSocket.OPEN:
        return this._send(message);

      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        return this._queue(message);

      default:
        console.debug('Unknown WebSocket state:', this.socket.readyState);
        this.connect(100);
        this._queue(message);
        return undefined;
    }
  }

  // We assume all normal checks have been done before calling
  // the internal _send function. So we do a try catch to recover
  // from completely unexpected errors. No retries. No nothing.
  // just recover and continue.
  _send(message: unknown): SendStatus {
    try {
      this.socket!.send(JSON.stringify(message));
      return this.SENT; // ✅ Success
    } catch (error) {
      console.debug('Send failed:', error);
      return this.FAILED;
    }
  }

  _queue(message: unknown): SendStatus {
    this._messageQueue.push(message);
    return this.QUEUED;
  }

  flushQueue() {
    if (this._messageQueue.length === 0) return;
    const failedMessages: unknown[] = [];

    while (this._messageQueue.length > 0) {
      const message = this._messageQueue.shift(); // Remove from front
      const result = this._send(message);
      if (result !== this.SENT) failedMessages.push(message);
    }

    this._messageQueue.push(...failedMessages);
  }

  openHandler() {
    this.reconnectAttempts = 0;
    this.flushQueue();
    this.startHeartbeat();
  }

  closeHandler(event: CloseEvent) {
    this.stopHeartbeat();

    // Don't reconnect if session is not authenticated
    if (!this.session.isAuthenticated) {
      console.debug(
        'Session not authenticated, stopping WebSocket reconnection',
      );
      this.socket = undefined;
      return;
    }

    switch (event.code) {
      case 1006: // Network issue
        this.connect(1000); // Quick retry
        break;
      case 1012: // Service restart
        this.connect(5000); // Give time to restart
        break;
      case 1013: // Try again later
        this.connect(30000, false); // Server asked to wait
        break;
      case 1011: // Server error
      case 1014: // Bad gateway
        this.connect(5000);
        break;
      default: // Do not Reconnect
        this.socket = undefined;
        break;
    }
  }

  errorHandler(event: Event) {
    console.debug('Websocket Error: ', event);
  }

  messageHandler(event: MessageEvent<unknown>) {
    this.pushMessage(event.data as string);
  }

  setupVisibilityHandling() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.handleTabHidden();
      } else {
        this.handleTabVisible();
      }
    });
  }

  handleTabHidden() {
    // Do Nothing for now.
  }

  handleTabVisible() {
    this.reconnectAttempts = 0;
    this.connect();
  }

  setupBFCacheHandling() {
    window.addEventListener('pagehide', (event) => {
      if (event.persisted) {
        this.close('Page going to bfcache');
      }
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page loaded from BFCache
        this.reconnectAttempts = 0;
        this.connect(100);
      }
    });
  }

  setupNetworkHandling() {
    window.addEventListener('online', () => {
      this.reconnectAttempts = 0;
      this.connect(100);
    });
  }

  startHeartbeat() {
    // Websockets get disconnected after 10 minutes of inactivity.
    // We get a guaranteed disconnect after 2 hours.
    // https://docs.aws.amazon.com/apigateway/latest/developerguide/apigateway-execution-service-websocket-limits-table.html
    // https://repost.aws/questions/QUFbNpkJJvTySYuHA7uHQAeQ/extend-websocket-idle-connection-timeout
    console.info('Starting Heartbeat');
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 9 * 60000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
  }

  /*
   * RECOND HANDLING
   */
  removeRecordFromData(item: SocketItem) {
    const removeId = item.data!.id;
    const removeType = modelNameForType(item.data!.type);
    const removeItem = this.store.peekRecord(
      removeType,
      `${removeId}`,
    ) as SocketRecord | null;
    if (
      removeItem &&
      removeItem.currentState!.stateName !== 'root.deleted.inFlight'
    ) {
      removeItem.unloadRecord();
    }
    return;
  }

  pushMessage(data: string) {
    let dataJSON: ConversationMessageFrame | SocketItem[];

    try {
      dataJSON = JSON.parse(data);
    } catch {
      console.debug('Could not parse Websocket Message: ', data);
      return;
    }

    if (!Array.isArray(dataJSON)) {
      if (dataJSON.action === 'conversation.message')
        return this.handleMessageUpdate(dataJSON);
      return;
    }

    if ((dataJSON[0]?.data as unknown[] | undefined)?.length === 0) return;
    dataJSON.forEach((item) => {
      /**
       * If empty update object
       */
      if (!item?.data) return;

      if (
        item?.data?.type === 'TARGET' ||
        item?.data?.type === 'METRIC' ||
        item?.data?.type === 'MEASUREMENT' ||
        item?.data?.type === 'AGGREGATE'
      ) {
        return;
      }
      /**
       * IF YOUR OWN UPDATE THEN IGNORE
       */
      if (
        item.data.attributes?.updated_by?.type === 'PERSON' &&
        item.data.attributes?.updated_by?.id === this.currentUser.id &&
        item.data.type !== 'MESSAGE' &&
        item.data.type !== 'SCAN'
      ) {
        return;
      }

      /**
       * REMOVE/DELETE EVENT
       */
      if (item.event === 'remove') {
        this.removeRecordFromData(item);
        return;
      }

      /**
       * IF attributes field missing
       */
      if (!item?.data?.attributes) return;

      const storeItem = this.store.peekRecord(
        modelNameForType(item.data.type),
        `${item.data.id}`,
      ) as SocketRecord | null;

      const state = storeItem?.currentState?.stateName;

      const incomingUpdatedAt = item.data.attributes?.updated_at;
      const storeUpdatedAt = storeItem?.updated_at;

      // An update is **Stale** if the store already has a timestamp AND either:
      // - the incoming event has no timestamp (older than what we have), or
      // - the incoming timestamp is older than the one in the store
      const isStaleUpdate =
        storeUpdatedAt &&
        (!incomingUpdatedAt ||
          new Date(incomingUpdatedAt) < new Date(storeUpdatedAt));

      if (isStaleUpdate) return;

      if (
        storeItem &&
        state &&
        (state === 'root.deleted.saved' || state === 'root.deleted.inFlight')
      ) {
        return;
      } else if (storeItem) {
        // Backfilling a relationship the websocket message contained (see
        // peekElseFetchItem) is a plain GET that can fail on an ordinary
        // network blip — most commonly while this tab sits backgrounded for
        // a long-running scan. That's not actionable, so it's downgraded
        // but monitored on Sentry at 'info' level until we are certain we can
        // stop monitoring.
        this.updateStoreBykey(storeItem, item).catch((error) => {
          Sentry.captureException(error, { level: 'info' });
        });
      } else {
        const modelName = modelNameForType(item.data.type);
        const normalizedItem = this.store.normalize(
          modelName,
          item.data as unknown as Parameters<StoreService['normalize']>[1],
        );
        this.store.push(normalizedItem);
      }
    });
  }

  @action
  async peekElseFetchItem({ id, type }: Partial<RelationshipRef>) {
    if (!type) return;
    const _type = modelNameForType(type);
    const item =
      this.store.peekRecord(_type, id as string) ??
      (await this.store.findRecord(_type, id as string));
    return item;
  }

  @action
  async updateStoreBykey(storeItem: SocketRecord, item: SocketItem) {
    const belongsToRelationships =
      storeItem.constructor?.relationshipNames!.belongsTo;
    const hasManyRelationships =
      storeItem.constructor?.relationshipNames?.hasMany;
    const attributes = item.data!.attributes!;
    delete attributes['id'];
    const relationships = item.data!.relationships!;
    Object.keys(attributes).forEach((key) => {
      storeItem.set(key, attributes[key]);
    });
    for (let i = 0; i < belongsToRelationships.length; i++) {
      const key = belongsToRelationships[i]!;
      if (key === 'integration-links') break;
      if (!relationships[key]) {
        storeItem.set(key, null);
        continue;
      }
      const relationshipItem = await this.peekElseFetchItem(
        relationships[key].data as RelationshipRef,
      );
      if (relationshipItem) storeItem.set(key, relationshipItem);
      else continue;
    }

    for (let i = 0; i < hasManyRelationships!.length; i++) {
      const key = hasManyRelationships![i]!;
      if (key === 'integration-links') break;
      const related = relationships[key]?.data;
      if (Array.isArray(related)) {
        const arr: unknown[] = [];
        for (let j = 0; j < related.length; j++) {
          const relationshipItem = await this.peekElseFetchItem(related[j]!);
          if (relationshipItem) arr.push(relationshipItem);
        }
        storeItem.set(key, arr);
      }
    }
  }

  handleMessageUpdate(parsedData: ConversationMessageFrame) {
    if (parsedData.action !== 'conversation.message') return;
    const message = parsedData.message;
    const isComplete = parsedData.complete;
    const conversation_id = (
      message.relationships!['conversation']!.data as RelationshipRef
    ).id;
    const conversation = this.store.peekRecord(
      'conversation',
      conversation_id,
    ) as ConversationRecord;
    conversation.complete = isComplete;
    const normalizedItem = this.store.normalize(
      'message',
      parsedData.message as unknown as Parameters<StoreService['normalize']>[1],
    );

    //Swap the locally created pending message with the pushed one
    if (message.attributes!['type'] == 'USER') {
      const pendingNewMessage = conversation.messages.content.filter(
        (m) => m.isNew,
      )?.[0];
      if (pendingNewMessage) pendingNewMessage.deleteRecord();
    }

    this.store.push(normalizedItem);
  }
}
